import { Button, Checkbox, Drawer, Space } from 'antd';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { TQuestionInPoll } from 'shared/types/poll.types';
import { PollAnswerType } from 'shared/enums/poll';
import RequredField from 'shared/components/title/Title';
import PossibleAnwers from '../createPoll/possibleAnswers/PossibleAnwers';
import style from './index.module.scss';

export type TPollAnswer = {
  id: number;
  value: string;
  respondentsCount?: number;
};

type TQuestionDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: TQuestionInPoll) => void;
  question?: TQuestionInPoll;
};

const QuestionDrawer: FC<TQuestionDrawerProps> = ({ isOpen, onClose, onCreate, question }) => {
  const [isSeveralAnswers, setIsSeveralAnswers] = useState<boolean>(false);
  const [isOwnAnswerPossible, setIsOwnAnswerPossible] = useState<boolean>(false);
  const [answers, setAnswers] = useState<TPollAnswer[]>([]);
  const [title, setTitle] = useState<string>('');

  useEffect(() => {
    if (question) {
      setIsSeveralAnswers(question.answerType === PollAnswerType.CHECKBOX ? true : false);
      setIsOwnAnswerPossible(question.isYourAnswer);
      setAnswers(question.answers);
      setTitle(question.title);
    }
  }, [question, isOpen]);

  const validateAnswers = () => {
    return answers.every(elem => !!elem.value);
  };

  const validatePoll = (): boolean => {
    const newAnswers = answers.filter(elem => elem !== undefined);
    return !!title && !!newAnswers.length && validateAnswers();
  };

  const onAddAnswer = () => {
    const answer = { id: Date.now(), value: '' };
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
  };

  const onDeleteAnswer = (id: number) => {
    const newAnswers = [...answers];
    newAnswers.splice(id, 1);
    setAnswers(newAnswers);
  };

  const onAnswerChange = (data: TPollAnswer[]) => {
    setAnswers(data);
  };

  const onTitleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTitle(event.target.value);
  };

  const onCancel = () => {
    setIsSeveralAnswers(false);
    setIsOwnAnswerPossible(false);
    setAnswers([]);
    setTitle('');
    onClose();
  };

  const onOk = () => {
    const filteredAnswers: TPollAnswer[] = answers.filter(elem => !!elem.value);
    const question: TQuestionInPoll = {
      id: Date.now(),
      title,
      answerType: isSeveralAnswers ? PollAnswerType.CHECKBOX : PollAnswerType.RADIO,
      isYourAnswer: isOwnAnswerPossible,
      answers: filteredAnswers,
    };
    onCreate(question);
    onCancel();
  };

  return (
    <Drawer
      title="Добавление вопроса"
      onClose={onCancel}
      open={isOpen}
      width={720}
      extra={
        <Space>
          <Button onClick={onCancel}>Отмена</Button>
          <Button type="primary" onClick={onOk} disabled={!validatePoll()}>
            Ок
          </Button>
        </Space>
      }
    >
      <RequredField onTitleChange={onTitleChange} title={title} />

      <PossibleAnwers
        answers={answers}
        onAddAnswer={onAddAnswer}
        onDeleteAnswer={onDeleteAnswer}
        onAnswerChange={onAnswerChange}
      />
      <div className={style.checkboxes}>
        <div>
          <Checkbox onChange={e => setIsSeveralAnswers(e.target.checked)} checked={isSeveralAnswers}>
            Возможность выбрать несколько вариантов ответа
          </Checkbox>
        </div>

        <div>
          <Checkbox onChange={e => setIsOwnAnswerPossible(e.target.checked)} checked={isOwnAnswerPossible}>
            Добавить пункт “Свой вариант ответа”
          </Checkbox>
        </div>
      </div>
    </Drawer>
  );
};

export default QuestionDrawer;
